import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  FileEdit,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  Star,
  Tag,
  AlertCircle,
  CheckCircle,
  Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const LETTER_TYPES = [
  { value: 'general', label: 'General', color: 'bg-slate-100 text-slate-700' },
  { value: 'bureau', label: 'Bureau Letters', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'furnisher', label: 'Furnisher Letters', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'identity_theft', label: 'Identity Theft', color: 'bg-red-100 text-red-700' },
  { value: 'inaccurate_reporting', label: 'Inaccurate Reporting', color: 'bg-amber-100 text-amber-700' },
  { value: 'balance_dispute', label: 'Balance Dispute', color: 'bg-amber-100 text-amber-700' },
  { value: 'personal_info', label: 'Personal Info Correction', color: 'bg-amber-100 text-amber-700' },
  { value: 'address_correction', label: 'Address Correction', color: 'bg-pink-100 text-pink-700' },
];

const CONTENT_TYPES = [
  { value: 'example', label: 'Example Letter' },
  { value: 'guidance', label: 'Writing Guidance' },
  { value: 'prompt', label: 'Prompt Instructions' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'specialist_notes', label: 'Specialist Notes' },
];

const getTypeBadge = (type) => {
  const lt = LETTER_TYPES.find(l => l.value === type);
  return lt ? (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${lt.color}`}>
      {lt.label}
    </span>
  ) : (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      {type}
    </span>
  );
};

const LetterTraining = () => {
  const { user, getAuthHeaders } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [letterTypes, setLetterTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [newMaterial, setNewMaterial] = useState({
    title: '',
    letter_type: 'general',
    content: '',
    content_type: 'example',
    effectiveness_rating: null,
    tags: '',
    notes: ''
  });

  useEffect(() => {
    fetchMaterials();
  }, [selectedType]);

  const fetchMaterials = async () => {
    try {
      // Use new admin-ops endpoint
      let url = `${API}/admin-ops/letter-examples`;
      if (selectedType && selectedType !== 'all') {
        url += `?letter_type=${selectedType}`;
      }
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMaterials(response.data.examples || []);
      // Extract unique letter types from examples
      const types = [...new Set((response.data.examples || []).map(e => e.letter_type))];
      setLetterTypes(types);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
      toast.error('Failed to load letter training materials');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newMaterial.title.trim() || !newMaterial.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const materialData = {
        ...newMaterial,
        tags: newMaterial.tags.split(',').map(t => t.trim()).filter(Boolean),
        effectiveness_rating: newMaterial.effectiveness_rating ? parseInt(newMaterial.effectiveness_rating) : null
      };
      // Use new admin-ops endpoint
      await axios.post(`${API}/admin-ops/letter-examples`, materialData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Training material created');
      setShowAddDialog(false);
      setNewMaterial({
        title: '',
        letter_type: 'general',
        content: '',
        content_type: 'example',
        effectiveness_rating: null,
        tags: '',
        notes: ''
      });
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to create material');
    }
  };

  const handleUpdate = async () => {
    if (!editingMaterial) return;

    try {
      const materialData = {
        ...editingMaterial,
        tags: typeof editingMaterial.tags === 'string'
          ? editingMaterial.tags.split(',').map(t => t.trim()).filter(Boolean)
          : editingMaterial.tags,
        effectiveness_rating: editingMaterial.effectiveness_rating 
          ? parseInt(editingMaterial.effectiveness_rating) 
          : null
      };
      // Use new admin-ops endpoint
      const materialId = editingMaterial.material_id || editingMaterial.id;
      await axios.patch(`${API}/admin-ops/letter-examples/${materialId}`, materialData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Material updated');
      setEditingMaterial(null);
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to update material');
    }
  };

  const handleDelete = async (materialId) => {
    if (!confirm('Are you sure you want to delete this training material?')) return;

    try {
      // Use new admin-ops endpoint
      await axios.delete(`${API}/admin-ops/letter-examples/${materialId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Material deleted');
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  // Group materials by type
  const materialsByType = materials.reduce((acc, m) => {
    if (!acc[m.letter_type]) acc[m.letter_type] = [];
    acc[m.letter_type].push(m);
    return acc;
  }, {});

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={12}
            className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}
          />
        ))}
      </div>
    );
  };

  if (!['admin', 'specialist', 'platform_owner', 'platform_admin', 'owner'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-stone-900 mb-2">Admin Access Required</h2>
        <p className="text-slate-500">This page is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="letter-training">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-3">
            <FileEdit className="text-amber-500" />
            Letter Generation Training
          </h1>
          <p className="text-sm text-slate-500">Improve dispute letter quality with training materials</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-material-btn">
          <Plus size={16} className="mr-2" />
          Add Material
        </Button>
      </div>

      {/* Filters */}
      <div className="elite-card p-4">
        <div className="flex gap-4">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Letter Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Letter Types</SelectItem>
              {LETTER_TYPES.map(lt => (
                <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchMaterials}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{materials.length}</p>
          <p className="text-xs text-slate-500">Total Materials</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">
            {materials.filter(m => m.content_type === 'example').length}
          </p>
          <p className="text-xs text-slate-500">Example Letters</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">
            {materials.filter(m => m.content_type === 'guidance').length}
          </p>
          <p className="text-xs text-slate-500">Guidance Docs</p>
        </div>
        <div className="elite-card p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{letterTypes.length}</p>
          <p className="text-xs text-slate-500">Letter Types</p>
        </div>
      </div>

      {/* Materials by Type */}
      {loading ? (
        <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : materials.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <FileEdit size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900">No Training Materials</h3>
          <p className="text-slate-500">Add example letters and guidance to improve letter generation</p>
        </div>
      ) : (
        <Tabs defaultValue={Object.keys(materialsByType)[0] || 'all'} className="space-y-4">
          <TabsList className="flex flex-wrap gap-1">
            {Object.keys(materialsByType).map(lt => {
              const typeInfo = LETTER_TYPES.find(t => t.value === lt);
              return (
                <TabsTrigger key={lt} value={lt} className="text-sm">
                  {typeInfo?.label || lt}
                  <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-slate-200 rounded-full">
                    {materialsByType[lt].length}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.entries(materialsByType).map(([letterType, typeMaterials]) => (
            <TabsContent key={letterType} value={letterType}>
              <div className="grid gap-4">
                {typeMaterials.map(material => (
                  <div key={material.id || material.material_id} className="elite-card p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-stone-900">{material.title}</h3>
                          {getTypeBadge(material.letter_type)}
                          <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded">
                            {CONTENT_TYPES.find(c => c.value === material.content_type)?.label || material.content_type}
                          </span>
                          {material.effectiveness_rating && renderStars(material.effectiveness_rating)}
                          {!material.is_active && (
                            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 font-mono bg-slate-50 p-2 rounded mb-2 max-h-24 overflow-y-auto">
                          {material.content?.substring(0, 300)}...
                        </p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Created: {new Date(material.created_at).toLocaleDateString()}</span>
                          {material.notes && <span>Notes: {material.notes.substring(0, 40)}...</span>}
                          {material.tags?.length > 0 && (
                            <div className="flex items-center gap-1">
                              <Tag size={12} />
                              {material.tags.slice(0, 3).map((tag, i) => (
                                <span key={i} className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingMaterial({
                            ...material,
                            tags: material.tags?.join(', ') || ''
                          })}
                        >
                          <Edit3 size={14} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(material.id || material.material_id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Training Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={newMaterial.title}
                  onChange={(e) => setNewMaterial({...newMaterial, title: e.target.value})}
                  placeholder="Material title"
                />
              </div>
              <div className="space-y-2">
                <Label>Letter Type</Label>
                <Select value={newMaterial.letter_type} onValueChange={(v) => setNewMaterial({...newMaterial, letter_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LETTER_TYPES.map(lt => (
                      <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select value={newMaterial.content_type} onValueChange={(v) => setNewMaterial({...newMaterial, content_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(ct => (
                      <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Effectiveness Rating (1-5)</Label>
                <Select 
                  value={newMaterial.effectiveness_rating?.toString() || 'none'} 
                  onValueChange={(v) => setNewMaterial({...newMaterial, effectiveness_rating: v === 'none' ? null : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Rated</SelectItem>
                    {[1, 2, 3, 4, 5].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} Star{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={newMaterial.content}
                onChange={(e) => setNewMaterial({...newMaterial, content: e.target.value})}
                placeholder="Paste example letter, guidance text, or training material..."
                rows={12}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={newMaterial.tags}
                  onChange={(e) => setNewMaterial({...newMaterial, tags: e.target.value})}
                  placeholder="cfpb, strong, effective"
                />
              </div>
              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Input
                  value={newMaterial.notes}
                  onChange={(e) => setNewMaterial({...newMaterial, notes: e.target.value})}
                  placeholder="Notes about this material"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Add Material</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingMaterial} onOpenChange={() => setEditingMaterial(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Training Material</DialogTitle>
          </DialogHeader>
          {editingMaterial && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingMaterial.title}
                    onChange={(e) => setEditingMaterial({...editingMaterial, title: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Letter Type</Label>
                  <Select value={editingMaterial.letter_type} onValueChange={(v) => setEditingMaterial({...editingMaterial, letter_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LETTER_TYPES.map(lt => (
                        <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Content Type</Label>
                  <Select value={editingMaterial.content_type} onValueChange={(v) => setEditingMaterial({...editingMaterial, content_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTENT_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Effectiveness Rating</Label>
                  <Select 
                    value={editingMaterial.effectiveness_rating?.toString() || ''} 
                    onValueChange={(v) => setEditingMaterial({...editingMaterial, effectiveness_rating: v || null})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Not Rated" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Rated</SelectItem>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} Star{n > 1 ? 's' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Content</Label>
                <Textarea
                  value={editingMaterial.content}
                  onChange={(e) => setEditingMaterial({...editingMaterial, content: e.target.value})}
                  rows={12}
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={editingMaterial.tags}
                  onChange={(e) => setEditingMaterial({...editingMaterial, tags: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Input
                  value={editingMaterial.notes || ''}
                  onChange={(e) => setEditingMaterial({...editingMaterial, notes: e.target.value})}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingMaterial.is_active}
                  onChange={(e) => setEditingMaterial({...editingMaterial, is_active: e.target.checked})}
                  className="rounded"
                />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMaterial(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Update Material</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LetterTraining;
